import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
import _extends from "@babel/runtime/helpers/extends";
var _excluded = ["fusionName"];
import moment from "moment";
import { singleLineAddress } from "../utils/address/address";
import * as constants from "../constants";
import { createMemoDeriveForm, externalModelUpdate } from "@icg360/rex/lib";
/**
 * Take a string and separate each 3rd character by a comma (from the right)
 *
 * @param {(string|number)} num - Number (as a string) you want to commafy
 * @return {string} Number (as a string) with commas every third character
 */

export function commafy(num) {
  if (typeof num !== "string" && typeof num !== "number" || num === "") {
    return "";
  }

  var separator = ",";
  return String(num).split("").reverse().reduce(function (prev, now, i) {
    return i % 3 === 0 ? prev + separator + now : prev + now;
  }).split("").reverse().join("");
}
/**
 * Take a string or number and format it, as best you can, as a currency, i.e.
 *  {string} 1234 -> $1,234.00
 *  {number} 14381.349 -> $14,381.34
 *  {string} asdf ->
 *  {string} -2.92 -> $2.92
 *  {string} 1.2 -> $1.20
 *
 * @param {(string|number)} value - The value we want to represent as a currency string
 * @param {string} showCents - Whether to show cents or not
 */

export function toCurrency(value, showCents) {
  if (showCents === void 0) {
    showCents = true;
  }

  // If it's not a string or number, we won't deal with it
  if (typeof value !== "string" && typeof value !== "number") {
    return "";
  } // Make sure what we're working with is a string


  var str = String(value); // Split it on the decimal into dollars and cents. If there's no decimal,
  // just set an empty string.

  var parts = str.split(".");
  var dollars = parts[0];
  var cents = parts[1] ? parts[1] : ""; // See if there's a `-` which would indicate a negative number

  var isNegative = dollars.indexOf("-") !== -1; // Normalize our dollars and cents by removing non-digits

  var regex = /\D/g;
  var dollarsNormalized = dollars.replace(regex, "");
  var centsNormalized = cents.replace(regex, ""); // Return an empty string if it's not ready to be a currency because stripping
  // away non-digits gave us an empty string

  if (dollarsNormalized === "" && centsNormalized === "") {
    return "";
  } // Add a comma every third character for the dollars


  var dollarsOut = commafy(dollarsNormalized); // Only show cents if applicable, and if it's more than 2 characters, trim it
  // If no cents came in and we're supposed to show them, we'll add '00'
  // If we only got one place after the decimal, we'll add a '0', i.e. "2.1" -> "2.10"

  var centsOut = showCents ? "." + (centsNormalized ? centsNormalized.substr(0, 2) : "00") : ""; // i.e. ".1", then add a zero

  if (centsOut.length === 2) {
    centsOut += "0";
  } // Return our currency string with negative value (if present) and dollar sign


  return (isNegative ? "-" : "") + "$" + dollarsOut + centsOut;
}
/**
 * Return a value formatted according to it's type (if applicable)
 * Empty values and unsupported types get the value returned as it came.
 *
 * @param {String} value
 *   The value you want to format
 * @param {String} type
 *   The type of the value. Supported types: currency, enum, date
 * @param {Object} enums
 *   An object that maps enumeration values to labels. Provides the ability to
 *   lookup an enum value to a user-friendly display value. i.e.
 *     { '100': 'Shutters', '200': 'No Shutters' }
 *   Only applicable to type enum.
 * @return {String}
 *   Value if empty; formatted value for relevant input types.
 */

export function formatValue(value, type, enums) {
  if (enums === void 0) {
    enums = {};
  }

  // Garbage in, garbage out.
  if (!value) {
    return value;
  } // Otherwise format it (if relevant)


  switch (type) {
    case "currency":
      // Format it as currency (with cents if they exist)
      return toCurrency(value, value.indexOf(".") !== -1);

    case "enum":
      // If there is no mapping of enums to look through or the lookup value
      // doesn't exist, just return the value as it came in
      return enums && enums[value] ? enums[value] : value;

    case "date":
      // It should be in the product format, since it's a dynamic value coming
      // from the product. Only try formatting it if the value exists
      return moment(value, constants.DATE_FORMAT_PRODUCT).format(constants.DATE_FORMAT_UI);

    default:
      return value;
  }
}
/**
 * Take an array of objects representing a list of enumerations and provide a
 * mapping of values to labels, i.e.
 *   [
 *     { value: '100', label: 'Something here' },
 *     { value: '200', label: 'Something here' }
 *   ]
 * Transforms to:
 *   {
 *     '100': 'Something here',
 *     '200': 'Something here'
 *   }
 *
 * @param {Array.<{label: String, value: String}>} enums
 * @return {Object} - mapping of values to labels
 */

export function mapEnumValuesToLabels(enums) {
  // If there's not at least one enum, don't bother. Just return undefined.
  return enums && enums.length > 0 && enums[0].label && enums[0].value ? enums.reduce(function (acc, enm) {
    var _extends2;

    return _extends({}, acc, (_extends2 = {}, _extends2[enm.value] = enm.label, _extends2));
  }, {}) : undefined;
}
/**
 * Get a description of the app for the requests to the server
 * @param {string} applicationId - Set in `configurePac`
 * @return {string}
 */

export function getAppDescription(applicationId) {
  var niceEnvName = constants.APP_NAMES[applicationId] ? constants.APP_NAMES[applicationId] : applicationId ? applicationId : "";
  return "posted by the Policy Actions Component via " + niceEnvName;
}
/**
 * Take an object and turn it into a query string.
 * NOTE: Does not support arrays at the moment.
 *
 * @param {Object} params - Object of key/val pairs we want as a query string
 * @return {string} - URI encoded string of the params
 */

export function getQueryString(params) {
  return Object.keys(params).map(function (key) {
    return encodeURIComponent(key) + "=" + encodeURIComponent(params[key]);
  }).join("&");
}
/**
 * Parse the error the server returned and return an error object we can use
 *
 * @param {Object} res - Response object detailing what went wrong in the request
 * @param {Object} resText - Response text (likely HTML)
 * @return {Object} A transactionRequestError object
 */

export function parseTransactionRequestError(res, resText) {
  return res && res.status !== 0 ? parseTransactionRequestErrorHtml(res, resText) : createAlertErrorMsg({
    title: res ? res.status + " " + res.statusText : "Timeout Error",
    description: "Sorry, but an error occurred while trying to process the transaction."
  });
}
/**
 * Parse error message from a server HTML response
 *
 * @param {Object} res - Response object
 * @param {Object} resText - Response text (likely HTML)
 * @return {Object} A transactionRequestError object
 */

export function parseTransactionRequestErrorHtml(res, resText) {
  // Assemble error message object
  var title = "";
  var description = "";
  var html = ""; // The error response comes back as HTML, which we need to pull apart
  // into a meaningful message of some sort using jQuery.

  var el = document.createElement("html");
  el.innerHTML = resText; // Set the title

  var h1 = el.querySelector("h1");
  title = h1 ? h1.textContent : "Error"; // Note sure if we need this part from policy-central...
  // If we didn't receive an X-True-Statuscode header then we prepend the
  // HTTP status code to the title.
  // if (!jqXHR.getResponseHeader('X-True-Statuscode')) {
  //     error.title = error.title + ' ' + jqXHR.status;
  // }
  // Set the description if there are description items to be found

  var paragraphs = el.querySelectorAll("p");

  if (paragraphs.length > 0) {
    [].slice.call(paragraphs).forEach(function (p) {
      description = description + p.textContent + " ";
    });
    description = description.trim();
  } // Set the html (if applicable)
  // We need to check the error message for lists (ol/ul). Some of the
  // services incorrectly send back <ul>s so we need to check both, or
  // set html to null if neither are present.


  var list = el.querySelector("ol") || el.querySelector("ul");
  html = list ? list.outerHTML : "";
  return createAlertErrorMsg({
    title: title,
    description: description,
    html: html
  });
}
/**
 * Create a generic UI error object that we can use for displaying errors in the
 * UI that map to the style of an <Alert>
 *
 * @param  {Object} error - same structure as return error type
 * @return {Object} error
 *         {string} error.title
 *         {string} error.description
 *         {string} error.html - A string of valid <html> markup
 *         {string} error.type - <Alert> style
 */

export function createAlertErrorMsg(error) {
  if (error === void 0) {
    error = {
      title: "",
      description: "",
      html: "",
      type: ""
    };
  }

  // If there is not error type included, make it danger by default
  if (!error.type) {
    error.type = "danger";
  }

  return error;
}
export var flattenInputs = function flattenInputs(inputs) {
  return inputs.reduce(function (acc, input) {
    if (input.type === "repeat") {
      return input.inputs.reduce(function (repeatAcc, repeatInputs) {
        return [].concat(repeatAcc, flattenInputs(repeatInputs));
      }, acc);
    }

    if (input.inputs) {
      return [].concat(acc, flattenInputs(input.inputs));
    }

    return [].concat(acc, [input]);
  }, []);
};

var inputHasChanged = function inputHasChanged(_ref) {
  var type = _ref.type,
      pristine = _ref.pristine;
  return !["output", "button", "submit"].includes(type) && !pristine;
};

export var filterChangedFieldsForCopy = function filterChangedFieldsForCopy(derivedForm) {
  var changedFields = [];
  var flattenedInputs = flattenInputs(derivedForm);
  flattenedInputs.forEach(function (input) {
    if (inputHasChanged(input)) {
      var updatedValue = formatValue(input.value, input.type, mapEnumValuesToLabels(input.details));

      if (!input.initialValue) {
        changedFields.push(input.label + " changed to " + updatedValue + ".");
      } else {
        var initValue = formatValue(input.initialValue, input.type, mapEnumValuesToLabels(input.details));
        changedFields.push(input.label + " changed from " + initValue + " to " + updatedValue + ".");
      }
    }
  });
  return changedFields;
};
export var filterChangedDerivedForm = function filterChangedDerivedForm(derivedForm) {
  return derivedForm.reduce(function (acc, item) {
    if (item.type === "repeat") {
      var repeat = item.inputs.reduce(function (repeatAcc, repeatInputs) {
        var filteredRepeatInputs = filterChangedDerivedForm(repeatInputs);

        if (filteredRepeatInputs.length) {
          repeatAcc.push(filteredRepeatInputs);
        }

        return repeatAcc;
      }, []);

      if (repeat.length) {
        acc.push(_extends({}, item, {
          inputs: repeat
        }));
      }

      return acc;
    }

    if (item.inputs) {
      var groupInputs = filterChangedDerivedForm(item.inputs);

      if (groupInputs.length) {
        acc.push(_extends({}, item, {
          inputs: groupInputs
        }));
      }

      return acc;
    }

    if (inputHasChanged(item)) {
      acc.push(item);
    }

    return acc;
  }, []);
};
export var fusionInputsToObject = function fusionInputsToObject(inputs) {
  return inputs.reduce(function (acc, _ref2) {
    var _extends3;

    var fusionName = _ref2.fusionName,
        input = _objectWithoutPropertiesLoose(_ref2, _excluded);

    return fusionName ? _extends({}, acc, (_extends3 = {}, _extends3[fusionName] = input, _extends3)) : acc;
  }, {});
};
export var reduceLocationMeta = function reduceLocationMeta(meta) {
  return meta == null ? void 0 : meta.reduce(function (acc, _ref3) {
    var _extends4;

    var name = _ref3.name,
        value = _ref3.value;
    return _extends({}, acc, (_extends4 = {}, _extends4[name] = value, _extends4));
  }, {});
};

var mapFusionInputsToObject = function mapFusionInputsToObject(inputs) {
  return inputs.reduce(function (acc, _ref4) {
    var _extends5;

    var fusionName = _ref4.fusionName,
        value = _ref4.value;
    return fusionName ? _extends({}, acc, (_extends5 = {}, _extends5[fusionName] = value, _extends5)) : acc;
  }, {});
};

export var formatAddress = function formatAddress(_ref5) {
  var _ref5$streetNumber = _ref5.streetNumber,
      streetNumber = _ref5$streetNumber === void 0 ? null : _ref5$streetNumber,
      _ref5$streetName = _ref5.streetName,
      streetName = _ref5$streetName === void 0 ? "" : _ref5$streetName,
      _ref5$city = _ref5.city,
      city = _ref5$city === void 0 ? "" : _ref5$city,
      _ref5$state = _ref5.state,
      state = _ref5$state === void 0 ? "" : _ref5$state,
      _ref5$zipcode = _ref5.zipcode,
      zipcode = _ref5$zipcode === void 0 ? "" : _ref5$zipcode;
  return streetNumber && city && state ? singleLineAddress({
    streetNumber: streetNumber,
    route: streetName,
    city: city,
    state: state,
    postalCode: zipcode
  }) : null;
};
export var getAddressFusionDetails = function getAddressFusionDetails(groups) {
  return groups.reduce(function (acc, _ref6) {
    var inputs = _ref6.inputs,
        label = _ref6.label;
    var location = inputs.find(function (_ref7) {
      var fusion = _ref7.fusion;
      return fusion === "location";
    });
    var address = inputs.find(function (_ref8) {
      var fusion = _ref8.fusion;
      return fusion === "address";
    });

    if (location) {
      var locationData = mapFusionInputsToObject(location.inputs);
      acc[label] = {
        locationNumber: locationData.locationNumber,
        premisesNumber: locationData.premisesNumber,
        buildingNumber: locationData.buildingNumber
      };
    }

    if (address) {
      var addressData = mapFusionInputsToObject(address.inputs);
      mapFusionInputsToObject(address.inputs);
      acc[label].address = formatAddress(addressData);
    }

    return acc;
  }, {});
};
export var changeAllPremisesBuildingNumbers = function changeAllPremisesBuildingNumbers(_ref9) {
  var formId = _ref9.formId,
      addressFusionDetails = _ref9.addressFusionDetails,
      dispatch = _ref9.dispatch;
  var sortedListOfAddresses = Object.values(addressFusionDetails).sort(function (a, b) {
    return a.locationNumber - b.locationNumber;
  }).map(function (_ref10) {
    var address = _ref10.address;
    return address;
  });
  var nextNumbers = calculatePremisesBuildingNumbers(sortedListOfAddresses);
  dispatch(externalModelUpdate({
    data: premisesBuildingNumbersToTermNames(nextNumbers),
    formId: formId
  }));
};

var premisesBuildingNumbersToTermNames = function premisesBuildingNumbersToTermNames(premisesBuildingNumbers) {
  return premisesBuildingNumbers.reduce(function (acc, _ref11, index) {
    var _extends6;

    var premisesNumber = _ref11.premisesNumber,
        buildingNumber = _ref11.buildingNumber;
    return _extends({}, acc, (_extends6 = {}, _extends6["Location" + (index + 1) + "PremisesNumber"] = premisesNumber, _extends6["Location" + (index + 1) + "BuildingNumber"] = buildingNumber, _extends6));
  }, {});
};

var calculatePremisesBuildingNumbers = function calculatePremisesBuildingNumbers(addresses) {
  var _addresses$reduce = addresses.reduce(function (acc, address) {
    if (!address) {
      acc.sections.push({
        premisesNumber: "",
        buildingNumber: ""
      });
      return acc;
    }

    if (acc.locations[address]) {
      acc.locations[address].buildingCount += 1;
    } else {
      acc.locationCount += 1;
      acc.locations[address] = {
        locationVal: acc.locationCount,
        buildingCount: 1
      };
    }

    acc.sections.push({
      premisesNumber: acc.locations[address].locationVal.toString(),
      buildingNumber: acc.locations[address].buildingCount.toString()
    });
    return acc;
  }, {
    locations: {},
    locationCount: 0,
    sections: []
  }),
      sections = _addresses$reduce.sections;

  return sections;
};

export var memoDeriveForm = {
  quote: createMemoDeriveForm(),
  checkout: createMemoDeriveForm()
};